import { useEffect, useRef } from "react";

import type { DiagnosticTrack, ReportResponse } from "@/lib/api";

import {
  GATE_FRAMEWORK_URL,
  REPORT_GATE_FRAMEWORK,
  REPORT_MOVES,
  REPORT_WHITEPAPER,
  reportEyebrow,
  trackNumber,
  WHITEPAPER_URL,
} from "../copy";

interface ReportProps {
  /** How the visitor is greeted, as typed into the gate. */
  firstName: string;
  report: ReportResponse;
  /** The track names from the questions payload, used to title each row. */
  tracks: DiagnosticTrack[];
}

/** The GATE acronym, as a whole word, so "gateway" or "navigate" never match. */
const GATE_MENTION = /\bGATE\b/;

/** One move, with the GATE framework link appended when the move names it. */
function Move({ move }: { move: string }) {
  if (!GATE_MENTION.test(move)) {
    return <li className="mt-1.5 first:mt-0">{move}</li>;
  }
  return (
    <li className="mt-1.5 first:mt-0">
      {move}{" "}
      <a
        className="text-hw-blue underline underline-offset-2"
        href={GATE_FRAMEWORK_URL}
        rel="noopener noreferrer"
        target="_blank"
      >
        ({REPORT_GATE_FRAMEWORK})
      </a>
    </li>
  );
}

/**
 * The written diagnostic: a diagnosis and a score per track, three moves for
 * the weakest track, and the closing line.
 *
 * The eyebrow takes focus on mount. The card appears well below the fold on a
 * phone, and it replaces the form the visitor just submitted, so without this
 * a screen reader would stay on a button that is no longer there.
 */
export function Report({ firstName, report, tracks }: ReportProps) {
  const eyebrowRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    eyebrowRef.current?.focus({ preventScroll: true });
  }, []);

  return (
    <div className="mt-6">
      <p className="hw-mono outline-none" ref={eyebrowRef} tabIndex={-1}>
        {reportEyebrow(firstName)}
      </p>

      <div className="mt-3">
        {report.tracks.map((track) => (
          <div className="border-hw-line border-t py-3.5" key={track.track}>
            <span className="hw-h4 float-right pl-3">{report.per_track[track.track] ?? 0}</span>
            <p className="hw-subheading">
              {trackNumber(track.track)} · {tracks[track.track]?.name ?? ""}
            </p>
            <p className="hw-body text-hw-soft mt-1.5">{track.diagnosis}</p>
          </div>
        ))}
      </div>

      <div className="bg-hw-pale mt-3.5 rounded-[14px] p-4">
        <p className="hw-mono text-hw-blue">{REPORT_MOVES}</p>
        <ol className="hw-body text-hw-ink mt-2 list-decimal pl-5">
          {report.moves.slice(0, 3).map((move, position) => (
            <Move key={`${position}-${move}`} move={move} />
          ))}
        </ol>
      </div>

      <p className="hw-body mt-4">{report.closing}</p>

      <p className="mt-4">
        <a
          className="hw-label text-hw-mute hover:text-hw-blue underline underline-offset-4"
          href={WHITEPAPER_URL}
          rel="noopener noreferrer"
          target="_blank"
        >
          {REPORT_WHITEPAPER}
        </a>
      </p>
    </div>
  );
}
